import {
  canonicalizeShellPath,
  canonicalizeShellPathname,
  isPublicProfilePath,
  shellPaths,
} from '@/core/router/app-paths';
import { isMoreTabSubPath, resolveTabForPath } from '@/core/router/app-tabs';
import { getShellDetailParent } from '@/core/router/shell-route-config';

export type ShellNavigationIntent =
  | 'switchTab'
  | 'pushOnStack'
  | 'replaceFlat'
  | 'updateQuery'
  | 'coldDetail';

export type ShellNavigationStep = {
  path: string;
  direction: 'forward' | 'back' | 'root' | 'none';
  action: 'push' | 'pop' | 'replace';
};

export type ShellNavigationPlan = {
  intent: ShellNavigationIntent;
  steps: ShellNavigationStep[];
};

export type ResolveShellNavigationInput = {
  currentPathname: string;
  currentSearch: string;
  targetPath: string;
  isNative: boolean;
  intent?: ShellNavigationIntent;
  coldLink?: boolean;
};

function splitTargetPath(targetPath: string): {
  pathname: string;
  search: string;
  full: string;
} {
  const canonical = canonicalizeShellPath(targetPath);
  const [pathnamePart, searchPart = ''] = canonical.split('?');
  const pathname = pathnamePart ?? canonical;
  const search = searchPart ? `?${searchPart}` : '';
  return {
    pathname: canonicalizeShellPathname(pathname),
    search,
    full: `${pathname}${search}`,
  };
}

function isDescendantPath(parent: string, child: string): boolean {
  return child !== parent && child.startsWith(`${parent}/`);
}

/** True when `child` is a stacked sub-route under `parent` (e.g. lead detail under leads list). */
export function isStackedShellChild(parent: string, child: string): boolean {
  return isDescendantPath(parent, child);
}

/** More hub is the native stack root; desktop treats hub navigation as flat. */
function isMoreHubStackRoot(pathname: string, isNative: boolean): boolean {
  if (!isNative) {
    return false;
  }
  const path = canonicalizeShellPathname(pathname);
  return path === shellPaths.moreHub || path === shellPaths.more;
}

function buildColdDetailPlan(
  targetFull: string,
  parent: string,
): ShellNavigationPlan {
  return {
    intent: 'coldDetail',
    steps: [
      { path: parent, direction: 'none', action: 'replace' },
      { path: targetFull, direction: 'forward', action: 'push' },
    ],
  };
}

function buildPublicProfileScanPlan(
  targetFull: string,
  targetPathname: string,
  targetSearch: string,
  currentPathname: string,
  currentSearch: string,
): ShellNavigationPlan {
  if (isPublicProfilePath(currentPathname)) {
    if (targetPathname === currentPathname && targetSearch !== currentSearch) {
      return {
        intent: 'updateQuery',
        steps: [{ path: targetFull, direction: 'none', action: 'replace' }],
      };
    }

    return {
      intent: 'replaceFlat',
      steps: [{ path: targetFull, direction: 'forward', action: 'replace' }],
    };
  }

  return buildColdDetailPlan(targetFull, shellPaths.leads);
}

function buildPlanForIntent(
  intent: ShellNavigationIntent,
  input: ResolveShellNavigationInput,
  targetFull: string,
  targetPathname: string,
): ShellNavigationPlan {
  switch (intent) {
    case 'updateQuery':
      return {
        intent: 'updateQuery',
        steps: [{ path: targetFull, direction: 'none', action: 'replace' }],
      };
    case 'pushOnStack':
      return {
        intent: 'pushOnStack',
        steps: [{ path: targetFull, direction: 'forward', action: 'push' }],
      };
    case 'replaceFlat':
      return {
        intent: 'replaceFlat',
        steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
      };
    case 'switchTab':
      return {
        intent: 'switchTab',
        steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
      };
    case 'coldDetail': {
      const parent = getShellDetailParent(targetPathname);
      if (!parent) {
        return {
          intent: 'replaceFlat',
          steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
        };
      }
      return buildColdDetailPlan(targetFull, parent);
    }
    default:
      return {
        intent: 'replaceFlat',
        steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
      };
  }
}

/** Plans Ionic router steps for a shell navigation request. */
export function resolveShellNavigationPlan(
  input: ResolveShellNavigationInput,
): ShellNavigationPlan {
  const currentPathname = canonicalizeShellPathname(input.currentPathname);
  const currentSearch = input.currentSearch || '';
  const {
    pathname: targetPathname,
    search: targetSearch,
    full: targetFull,
  } = splitTargetPath(input.targetPath);

  if (input.intent) {
    return buildPlanForIntent(input.intent, input, targetFull, targetPathname);
  }

  if (input.coldLink) {
    const parent = getShellDetailParent(targetPathname);
    if (parent) {
      if (isPublicProfilePath(targetPathname)) {
        return buildPublicProfileScanPlan(
          targetFull,
          targetPathname,
          targetSearch,
          currentPathname,
          currentSearch,
        );
      }
      return buildColdDetailPlan(targetFull, parent);
    }
  }

  if (targetPathname === currentPathname && targetSearch !== currentSearch) {
    return {
      intent: 'updateQuery',
      steps: [{ path: targetFull, direction: 'none', action: 'replace' }],
    };
  }

  const currentTab = resolveTabForPath(currentPathname);
  const targetTab = resolveTabForPath(targetPathname);

  if (currentTab && targetTab && currentTab !== targetTab) {
    return {
      intent: 'switchTab',
      steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
    };
  }

  // Sidebar peers under the same tab (e.g. Alle Leads ↔ Events hub) must
  // replace, not push — `leadsEvents` is a descendant path of `leads`.
  if (
    targetPathname === shellPaths.leads ||
    targetPathname === shellPaths.leadsEvents
  ) {
    return {
      intent: 'replaceFlat',
      steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
    };
  }

  const detailParent = getShellDetailParent(targetPathname);
  if (
    isPublicProfilePath(targetPathname) &&
    isPublicProfilePath(currentPathname)
  ) {
    return buildPublicProfileScanPlan(
      targetFull,
      targetPathname,
      targetSearch,
      currentPathname,
      currentSearch,
    );
  }

  if (
    detailParent === currentPathname ||
    isDescendantPath(currentPathname, targetPathname)
  ) {
    return {
      intent: 'pushOnStack',
      steps: [{ path: targetFull, direction: 'forward', action: 'push' }],
    };
  }

  if (
    input.isNative &&
    isMoreHubStackRoot(currentPathname, input.isNative) &&
    isMoreTabSubPath(targetPathname)
  ) {
    return {
      intent: 'pushOnStack',
      steps: [{ path: targetFull, direction: 'forward', action: 'push' }],
    };
  }

  return {
    intent: 'replaceFlat',
    steps: [{ path: targetFull, direction: 'root', action: 'replace' }],
  };
}
